import React from 'react';

import {
  ClipboardListIcon,
  PhoneIcon,
  HomeIcon,
  CodeIcon,
  BadgeCheckIcon,
  CalendarIcon,
  ChatAlt2Icon,
  DotsHorizontalIcon,
  TerminalIcon,
  ArrowRightIcon,
  ClockIcon,
} from '@heroicons/react/outline';

import Link from './Link';
import Tooltip from './Tooltip';
import {cx} from '../util';
import {STAGES} from '../../../lib/utils';

export const STAGE_ICONS = {
  recruiter: ClipboardListIcon,
  online_assessment: TerminalIcon,
  phone: PhoneIcon,
  technical: CodeIcon,
  take_home: HomeIcon,
  final: BadgeCheckIcon,
  other: DotsHorizontalIcon,
};

const STAGES_MAP = STAGES.reduce((acc, s) => {
  acc[s.id] = s;
  return acc;
}, {});

export const DIFFICULTY_LABELS = {
  1: 'Very easy',
  2: 'Easy',
  3: 'Moderate',
  4: 'Difficult',
  5: 'Very difficult',
};

export const InfoBox = ({label, value, className}) => (
  <div
    className={cx(
      'border border-gray-100 shadow rounded-xl px-4 py-3 bg-white',
      className,
    )}
  >
    <div className="text-xs text-gray-400 mb-1">{label}</div>
    <div className="text-sm font-medium text-gray-900">{value}</div>
  </div>
);

export const DifficultyScale = ({difficulty, className}) => (
  <div
    className={cx(
      'border border-gray-100 shadow rounded-xl px-4 py-3 bg-white',
      className,
    )}
  >
    <div className="text-xs text-gray-400 mb-1">Difficulty</div>
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-900">
        {DIFFICULTY_LABELS[difficulty] || 'Moderate'}
      </span>
      <div className="flex items-end gap-0.5">
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={level}
            className={cx(
              'w-1.5 rounded-sm',
              level <= difficulty ? 'bg-indigo-500' : 'bg-gray-200',
            )}
            style={{height: `${level * 4}px`}}
          />
        ))}
      </div>
    </div>
  </div>
);

export const CompBreakdown = ({
  salary, stock, bonus, className,
}) => {
  const COMP_COMPONENTS = [
    {
      key: 'salary', label: 'Salary', value: salary, color: 'bg-indigo-500',
    },
    {
      key: 'stock', label: 'Stock', value: stock, color: 'bg-indigo-400',
    },
    {
      key: 'bonus', label: 'Bonus', value: bonus, color: 'bg-indigo-300',
    },
  ];
  const MIN_SLIVER = 3;

  const total = salary + stock + bonus;
  const nonZeroCount = COMP_COMPONENTS.filter((c) => c.value > 0).length;
  const zeroCount = COMP_COMPONENTS.length - nonZeroCount;
  const sliverTotal = zeroCount * MIN_SLIVER;
  const remainingPercent = 100 - sliverTotal;
  const percentages = COMP_COMPONENTS.map(
    (comp) => Math.max((comp.value / total) * remainingPercent, MIN_SLIVER),
  );

  return (
    <div
      className={cx(
        'border border-gray-100 shadow rounded-xl px-4 py-3 bg-white',
        className,
      )}
    >
      <div className="text-xs text-gray-400">Total comp</div>
      <div className="flex items-start gap-3">
        <div className="text-sm font-medium text-gray-900 shrink-0 mt-1">
          $
          {total.toLocaleString()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-0.5 h-2.5">
            {COMP_COMPONENTS.map((comp, idx) => (
              <Tooltip
                key={comp.key}
                content={`${comp.label}: $${comp.value.toLocaleString()}`}
                placement="top"
                className="h-full"
                style={{width: `${percentages[idx]}%`}}
              >
                <div
                  className={cx(
                    'h-full w-full cursor-pointer',
                    comp.color,
                    idx === 0 && 'rounded-l',
                    idx === COMP_COMPONENTS.length - 1 && 'rounded-r',
                  )}
                />
              </Tooltip>
            ))}
          </div>
          <div className="flex items-center gap-0.5">
            {COMP_COMPONENTS.map((comp, idx) => (
              <span
                key={comp.key}
                className="text-[10px] text-gray-400 text-center"
                style={{
                  width: `${percentages[idx]}%`,
                  visibility: percentages[idx] >= 15 ? 'visible' : 'hidden',
                }}
              >
                {comp.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const VerifiedBadge = () => (
  <Tooltip
    content="We conducted an interview with the candidate to verify this experience"
    placement="bottom"
  >
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 rounded-full px-2.5 py-1 cursor-default">
      <BadgeCheckIcon className="h-3.5 w-3.5" />
      Verified
    </span>
  </Tooltip>
);

export const SimpleBadge = ({className, ...props}) => (props.children
  ? <span className={cx('inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1', className || 'text-gray-600 bg-gray-100')} {...props} />
  : null);

export const LocationBadge = ({children}) => {
  if (!children) return null;
  return (
    <SimpleBadge className="text-gray-600 bg-white border border-gray-200">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      {children}
    </SimpleBadge>
  );
};

export const OlderFormatBadge = () => (
  <Tooltip content="This is an older submission and contains less detail than recent experiences" placement="bottom">
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 cursor-default">
      <ClockIcon className="w-3 h-3" />
      Older format
    </span>
  </Tooltip>
);

export const RoundsBadge = ({stages, count: countProp}) => {
  let count = countProp;
  if (count == null) {
    if (!stages || !Array.isArray(stages) || stages.length === 0) return null;
    count = stages.length;
  }
  if (!count) return null;
  return (
    <SimpleBadge>
      <ChatAlt2Icon className="h-3.5 w-3.5" />
      {count}
      {' '}
      {count !== 1 ? 'rounds' : 'round'}
    </SimpleBadge>
  );
};

export const QuestionsBadge = ({count}) => {
  if (!count) return null;
  return (
    <SimpleBadge>
      <ChatAlt2Icon className="h-3.5 w-3.5" />
      {count}
      {' '}
      {count !== 1 ? 'questions' : 'question'}
    </SimpleBadge>
  );
};

export const DurationBadge = ({value, unit}) => {
  if (!value || !unit) return null;
  const label = Number(value) === 1 ? unit.replace(/s$/, '') : unit;
  return (
    <SimpleBadge>
      <CalendarIcon className="h-3.5 w-3.5" />
      {value}
      {' '}
      {label}
    </SimpleBadge>
  );
};

export const QuestionsSection = ({stages}) => {
  const stagesWithQuestions = (stages || [])
    .filter((s) => s.questions?.some((q) => q.text?.trim()));

  if (stagesWithQuestions.length === 0) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Questions</h3>
      {stagesWithQuestions.map((s, idx) => {
        const stageDef = STAGES_MAP[s.name];
        const questions = s.questions.filter((q) => q.text?.trim());
        const Icon = STAGE_ICONS[s.name];

        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${s.name}-${idx}`} className="space-y-3">
            <div className="border-b-2 border-gray-100">
              <div className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 border-b-2 border-indigo-500 pb-1 -mb-2">
                {Icon && <Icon className="w-4 h-4" />}
                {stageDef?.label || s.name}
              </div>
            </div>
            <div className="space-y-4 pl-1">
              {questions.map((q) => (
                <div key={`${s.name}-${q.text}`}>
                  <p className="text-sm font-medium text-gray-900 mb-1">{q.text}</p>
                  {q.answer && (
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                      {q.answer}
                    </p>
                  )}
                  {q.existing && (
                    <Link
                      href={`/questions/${q.existing}`}
                      target="_blank"
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      See community answers
                      {' '}
                      <ArrowRightIcon className="w-3.5 h-3.5 inline" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ReflectionsSection = ({overview, tips, companyCulture}) => {
  const hasOverview = overview?.trim();
  const hasTips = tips?.trim();
  const hasCompanyCulture = companyCulture?.trim();

  if (!hasOverview && !hasTips && !hasCompanyCulture) return null;

  const reflections = [
    {label: 'How was the overall interview process?', value: overview, visible: hasOverview},
    {label: 'What do you wish you knew before this interview process?', value: tips, visible: hasTips},
    {
      label: 'What did you learn about the company culture?',
      value: companyCulture,
      visible: hasCompanyCulture,
    },
  ].filter((r) => r.visible);

  return (
    <div className="space-y-4 mt-8">
      <h3 className="text-lg font-semibold text-gray-900">Reflections</h3>
      <div className="space-y-4">
        {reflections.map((reflection) => (
          <div key={reflection.label}>
            <p className="text-sm font-medium text-gray-900 mb-1">{reflection.label}</p>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{reflection.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
